  <div class="ocr-verification-report">
    <PageControl
      :title="'OCR Document Verification Report'"
      :elements_query="sheet_elements"
    >
      <button
        @click="show_export_modal"
        class="btn btn-bg-green text-xs rounded-small shadow-sm me-2"
      >
        <i class="bi bi-download"></i>
        Export Data
      </button>

      <!-- Export Modal -->
      <Teleport to="body">
        <div
          class="modal fade"
          :id="'export-modal-' + report_reference"
          tabindex="-1"
          aria-labelledby="exportModalLabel"
          aria-hidden="true"
        >
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content rounded-4 shadow">
            <div class="modal-header">
              <h6 class="modal-title fw-semibold text-muted">
                Export OCR Verification Report Data
              </h6>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label fw-bold">Select Data to Export:</label>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="export_options.document_details"
                    id="documentDetails"
                  />
                  <label class="form-check-label" for="documentDetails">
                    Document Details & Personal Information
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="export_options.authenticity_analysis"
                    id="authenticityAnalysis"
                  />
                  <label class="form-check-label" for="authenticityAnalysis">
                    Security Authentication Analysis
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="export_options.raw_json"
                    id="rawJson"
                  />
                  <label class="form-check-label" for="rawJson">
                    Raw JSON Response Data
                  </label>
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label fw-bold">Export Format:</label>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    v-model="export_format"
                    value="csv"
                    id="csvFormat"
                  />
                  <label class="form-check-label" for="csvFormat">
                    CSV (Separate files for each table)
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    v-model="export_format"
                    value="excel"
                    id="excelFormat"
                  />
                  <label class="form-check-label" for="excelFormat">
                    Excel (Multiple sheets in one file)
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    v-model="export_format"
                    value="json"
                    id="jsonFormat"
                  />
                  <label class="form-check-label" for="jsonFormat">
                    JSON (JavaScript Object Notation)
                  </label>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-bg-secondary text-xs rounded-small shadow-sm me-2"
                @click="close_modal"
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-bg-green text-xs rounded-small shadow-sm"
                @click="export_data"
                :disabled="!has_selected_tables"
              >
                <i class="bi bi-download me-1"></i>
                Export Data
              </button>
            </div>
          </div>
        </div>
      </div>
      </Teleport>
    </PageControl>
    <PageComponent>
      <SectionSheet>
        <div
          class="container-fluid py-3 header d-flex align-items-center justify-content-between border-bottom mb-3"
        >
          <div class="d-flex">
            <img
              class="logo"
              width="295"
              height="125"
              src="@/assets/images/new_black_logo2.svg"
              alt="Logo"
            />
          </div>
          <div class="d-flex text-end">
            <div>
              <div class="h4 fw-bold mb-1">OCR Document Verification</div>
              <div class="text-muted text-xs">
                28 Fricker Rd,<br />
                Illovo, Johannesburg,<br />
                2196 South Africa<br />
                Website:
                <a
                  href="https://www.goodpeopledata.com/"
                  target="_blank"
                  class="text-decoration-none text-muted"
                  >www.goodpeopledata.com</a
                >
              </div>
            </div>
          </div>
        </div>

        <div class="card shadow-sm border bg-white mb-2">
          <div class="card-header">
            <h6 class="mb-0">OCR Verification Summary</h6>
          </div>
          <div class="card-body">
            <p>
              This OCR verification was conducted on
              <span class="fw-bold text-dark">{{ display_full_name }}</span
              >, a
              <span class="fw-bold text-dark"
                >{{ age }}-year-old {{ formatted_sex }}</span
              >
              {{ formatted_citizenship_phrase }} of {{ issuing_country }}. The
              document analyzed was a
              <span class="fw-bold text-dark">{{ document_description }}</span>
              from
              <span class="fw-bold text-dark">{{ issuing_country }}</span> ({{
                document_year
              }}). The verification process examined {{ pages_count }} page(s)
              and evaluated multiple authenticity factors including security
              features, text analysis, image quality, and document type
              validation. The overall assessment result is detailed below:
            </p>

            <div class="card shadow-none p-2 border-0 bg-white mb-2">
              <div class="card-body text-center">
                <div class="provider-box">
                  OCR Verification Status:
                  <strong :class="overall_status_class">{{
                    overall_status_text
                  }}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card shadow-sm border bg-white mb-2">
          <div class="card-header">
            <h6 class="mb-0">Personal Information</h6>
          </div>
          <div class="card-body">
            <div class="row" v-if="Object.keys(document_details).length > 0">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label text-muted small">{{ is_driving_license ? 'Initials' : 'Full Name' }}</label>
                  <div class="fw-bold">{{ display_full_name }}</div>
                </div>
                <div class="mb-3" v-if="!is_driving_license">
                  <label class="form-label text-muted small">Given Names</label>
                  <div class="fw-bold">{{ given_names || document_details['Given Names'] || 'N/A' }}</div>
                </div>
                <div class="mb-3" v-if="!is_driving_license">
                  <label class="form-label text-muted small">Surname</label>
                  <div class="fw-bold">{{ surname || document_details['Surname'] || 'N/A' }}</div>
                </div>
                <div class="mb-3">
                  <label class="form-label text-muted small"
                    >Date of Birth</label
                  >
                  <div class="fw-bold">{{ formatted_date_of_birth || document_details['Date of Birth'] || document_details['DOB'] || 'N/A' }}</div>
                </div>
                <div class="mb-3">
                  <label class="form-label text-muted small">Age</label>
                  <div class="fw-bold">{{ age || document_details['Age'] || 'N/A' }} years old</div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label text-muted small">Sex</label>
                  <div class="fw-bold">{{ formatted_sex || (document_details['Sex'] === 'M' ? 'Male' : document_details['Sex'] === 'F' ? 'Female' : document_details['Sex'] || 'Unknown') }}</div>
                </div>
                <div class="mb-3">
                  <label class="form-label text-muted small"
                    >Personal Number</label
                  >
                  <div class="fw-bold">{{ personal_number || document_details['Personal Number'] || document_details['ID Number'] || 'N/A' }}</div>
                </div>
                <div class="mb-3">
                  <label class="form-label text-muted small"
                    >Citizenship Status</label
                  >
                  <div class="fw-bold">{{ formatted_citizenship_status }}</div>
                </div>
                <div class="mb-3" v-if="show_nationality_details">
                  <label class="form-label text-muted small">Nationality</label>
                  <div class="fw-bold">{{ formatted_nationality }}</div>
                </div>
                <div class="mb-3" v-if="show_nationality_details">
                  <label class="form-label text-muted small"
                    >Place of Birth</label
                  >
                  <div class="fw-bold">{{ place_of_birth || document_details['Place of Birth'] || 'N/A' }}</div>
                </div>
              </div>
            </div>

            <div v-else class="text-center text-muted py-4">
              <i class="fas fa-user-times fa-2x mb-2"></i>
              <p>No personal information available</p>
            </div>
          </div>
        </div>

        <div class="report-meta">
          <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
          <div>
            <span class="text-muted">Generated:</span> {{ generated_date }}
          </div>
        </div>
      </SectionSheet>
      <SectionSheet>
        <div class="card shadow-sm border-0 bg-white mb-2">
          <div class="card-header bg-gradient border-0">
            <h6 class="mb-0 fw-semibold text-dark d-flex align-items-center">
              
              <span>Document Images</span>
            </h6>
          </div>
          <div class="card-body p-3">
            <div v-if="document_image_items.length" class="document-images-grid">
              <!-- Document Front Side - Full Width -->
              <div
                v-for="image in document_image_items.filter(img => img.layout === 'full')"
                :key="image.key"
                class="mb-3"
              >
                <div class="document-image-wrapper">
                  <div class="image-container-full shadow-sm">
                    <img
                      v-if="image.hasImage"
                      :src="image.src"
                      :alt="image.label"
                      class="img-fluid document-image"
                    />
                    <div
                      v-else
                      class="no-image-placeholder"
                    >
                      <i class="fas fa-id-card fa-4x text-muted mb-3"></i>
                      <p class="text-muted mb-0">No {{ image.label ? image.label.toLowerCase() : 'image' }} available</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Portrait and Signature - Side by Side -->
              <div class="document-half-grid">
                <div
                  v-for="image in document_image_items.filter(img => img.layout === 'half')"
                  :key="image.key"
                  class="document-half-item"
                >
                  <div class="document-image-wrapper h-100">
                    <div class="image-container-half shadow-sm h-100">
                      <img
                        v-if="image.hasImage"
                        :src="image.src"
                        :alt="image.label"
                        class="img-fluid document-image"
                      />
                      <div
                        v-else
                        class="no-image-placeholder"
                      >
                        <i :class="image.label === 'Portrait' ? 'fas fa-user fa-3x text-muted mb-3' : image.label === 'Signature' ? 'fas fa-signature fa-3x text-muted mb-3' : 'fas fa-image fa-3x text-muted mb-3'"></i>
                        <p class="text-muted small mb-0">No {{ image.label ? image.label.toLowerCase() : 'image' }} available</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="text-center text-muted py-5">
              <i class="fas fa-images fa-4x mb-3 opacity-25"></i>
              <p class="mb-0 fw-semibold">No document images available</p>
            </div>
          </div>
        </div>

        <div class="report-meta">
          <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
          <div>
            <span class="text-muted">Generated:</span> {{ generated_date }}
          </div>
        </div>
      </SectionSheet>

      <SectionSheet>
        <div class="card shadow-sm border bg-white mb-2">
          <div class="card-header">
            <h6 class="mb-0">Document Information</h6>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label text-muted small"
                    >Document Type</label
                  >
                  <div class="fw-bold">{{ document_description }}</div>
                </div>
                <div class="mb-3">
                  <label class="form-label text-muted small"
                    >Issuing Country</label
                  >
                  <div class="fw-bold">{{ issuing_country }}</div>
                </div>
                <div class="mb-3">
                  <label class="form-label text-muted small"
                    >Document Year</label
                  >
                  <div class="fw-bold">{{ document_year }}</div>
                </div>
                <div class="mb-3">
                  <label class="form-label text-muted small">ICAO Code</label>
                  <div class="fw-bold">{{ icao_code }}</div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label text-muted small"
                    >Document Format</label
                  >
                  <div class="fw-bold">{{ document_format }}</div>
                </div>
                <div class="mb-3">
                  <label class="form-label text-muted small">Pages Count</label>
                  <div class="fw-bold">{{ pages_count }}</div>
                </div>
                <div class="mb-3">
                  <label class="form-label text-muted small">MRZ Present</label>
                  <div class="fw-bold">{{ mrz_present }}</div>
                </div>
                <div class="mb-3">
                  <label class="form-label text-muted small"
                    >Document Status</label
                  >
                  <div class="fw-bold">{{ document_status_text }}</div>
                </div>
                <div class="mb-3" v-if="display_date_of_issue !== 'N/A'">
                  <label class="form-label text-muted small">Date of Issue</label>
                  <div class="fw-bold">{{ display_date_of_issue }}</div>
                </div>
                <div class="mb-3" v-if="display_date_of_expiry !== 'N/A'">
                  <label class="form-label text-muted small">Date of Expiry</label>
                  <div class="fw-bold">{{ display_date_of_expiry }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="document_specific_sections.length"
          class="card shadow-sm border bg-white mb-2"
        >
          <div class="card-header">
            <h6 class="mb-0">Document Sections</h6>
          </div>
          <div class="card-body">
            <div
              v-for="(section, index) in document_specific_sections"
              :key="section.title"
              :class="['pt-3 border-top', { 'mt-3': index > 0 }]"
            >
              <h6 class="text-muted text-uppercase small mb-2">
                {{ section.title }}
              </h6>
              <div class="row">
                <div
                  class="col-md-6"
                  v-for="field in section.fields"
                  :key="field.label"
                >
                  <div class="mb-3">
                    <label class="form-label text-muted small"
                      >{{ field.label }}</label
                    >
                    <div class="fw-bold">{{ field.value }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="report-meta">
          <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
          <div>
            <span class="text-muted">Generated:</span> {{ generated_date }}
          </div>
        </div>
      </SectionSheet>

      <SectionSheet>
        <div class="card shadow-sm border bg-white mb-2">
          <div class="card-header">
            <h6 class="mb-0">Security Authentication Analysis</h6>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-12 mb-2">
                <p class="text-muted mb-2">
                  Comprehensive security assessment examining multiple
                  authentication factors to verify document integrity and
                  authenticity.
                </p>
              </div>
            </div>

            <!-- Summary Stats -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="security-summary-card border p-3">
                  <div class="row text-center">
                    <div class="col-4">
                      <div class="summary-metric">
                        <h4 class="mb-1 text-success fw-bold">
                          {{ passed_checks_count }}
                        </h4>
                        <small class="text-muted">Passed</small>
                      </div>
                    </div>
                    <div class="col-4">
                      <div class="summary-metric">
                        <h4 class="mb-1 text-info fw-bold">
                          {{ info_checks_count }}
                        </h4>
                        <small class="text-muted">Not Checked</small>
                      </div>
                    </div>
                    <div class="col-4">
                      <div class="summary-metric">
                        <h4 class="mb-1 text-danger fw-bold">
                          {{ failed_checks_count }}
                        </h4>
                        <small class="text-muted">Failed</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Security Checks Grid -->
            <div class="table-responsive mb-2">
              <table class="table align-middle">
                <thead>
                  <tr>
                    <th scope="col" class="fw-semibold">Security Check</th>
                    <th scope="col" class="fw-semibold text-center">Result</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="check in authenticity_checks" :key="check.name">
                    <td>
                      <div class="d-flex align-items-center">
                        <div>
                          <label class="mb-1 fw-semibold text-dark">{{
                            check.label
                          }}</label>
                        </div>
                      </div>
                    </td>
                    <td class="text-center">
                      <span
                        :class="
                          'fw-bold ' + get_status_color_class(check.value)
                        "
                      >
                        {{
                          check.value === 0
                            ? "Not Checked"
                            : check.value === 1
                            ? "Passed"
                            : check.value === 2
                            ? "Failed"
                            : check.value === 3
                            ? "Not Available"
                            : "Unknown"
                        }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Assessment Methodology -->
            <div class="row">
              <div class="col-12">
                <div class="methodology-note">
                  <div class="d-flex align-items-start">
                    <div>
                      <h6 class="mb-2 text-dark">Assessment Methodology</h6>
                      <p class="mb-0 text-muted small">
                        Each security feature is evaluated using advanced
                        algorithms that analyze document structure, security
                        elements, text integrity, and image quality to determine
                        authenticity. The assessment provides a comprehensive
                        evaluation of document legitimacy based on
                        industry-standard verification protocols.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="report-meta">
          <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
          <div>
            <span class="text-muted">Generated:</span> {{ generated_date }}
          </div>
        </div>
      </SectionSheet>
      <SectionSheet>
        <div class="card shadow-sm border bg-white mb-4 p-2">
          <div class="card-header">
            <h6 class="mb-0">Disclaimer & Important Information</h6>
          </div>
          <div class="card-body">
            <p class="text-xs mb-2">
              <strong>External Data Source:</strong> This OCR Document
              Verification Report contains data provided by an external service
              that has not been modified or changed in any way by
              <a
                href="https://www.goodpeopledata.com/"
                target="_blank"
                rel="noopener"
              >
                <strong>THE GOOD PEOPLE DATA COMPANY (PTY) LTD.</strong>
              </a>
              The accuracy and completeness of the verification results are
              dependent on the external service provider and various
              environmental factors.
            </p>
            <p class="text-xs mb-2">
              <strong>Verification Factors:</strong> The accuracy of OCR
              document verification is affected by multiple factors including
              image quality, lighting conditions, background environment,
              scanning environment, and the technology used for analysis. These
              factors may impact recognition accuracy and could lead to false
              positives or negatives in the verification results.
            </p>
          </div>
          <div class="provider-box my-3 text-center">
            OCR Verification report was generated by
            <a
              href="https://www.goodpeopledata.com/"
              target="_blank"
              rel="noopener"
            >
              <strong>THE GOOD PEOPLE DATA COMPANY (PTY) LTD.</strong>
            </a>
          </div>
        </div>

        <div class="report-meta">
          <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
          <div>
            <span class="text-muted">Generated:</span> {{ generated_date }}
          </div>
        </div>
      </SectionSheet>
    </PageComponent>
  </div>